<mat-card class="location-live ui-wrapper">
  <mat-card-title class="d-flex flex-wrap align-items-center gap-2">
    <span>Live Stock view</span>
    <mat-chip-list *ngIf="lastUpdated" class="ms-2">
      <mat-chip selected color="primary" disabled>
        Updated {{ lastUpdated | date : 'medium' }}
      </mat-chip>
    </mat-chip-list>
  </mat-card-title>

  <div class="toolbar mb-3" [formGroup]="filtersForm">
    <div class="location-toolbar-search-col">
      <mat-form-field appearance="outline" class="w-100">
        <mat-label>Search</mat-label>
        <input matInput formControlName="search" autocomplete="off" />
        <mat-icon matSuffix>search</mat-icon>
      </mat-form-field>
    </div>
    <div class="location-toolbar-refresh-toggle">
      <mat-slide-toggle formControlName="autoRefresh" color="primary">
        Auto-refresh (30s)
      </mat-slide-toggle>
    </div>
    <div class="location-toolbar-refresh-btn">
      <button
        mat-raised-button
        color="primary"
        type="button"
        [disabled]="loading"
        (click)="load()"
      >
        <mat-icon>refresh</mat-icon>
        Refresh
      </button>
    </div>
  </div>

  <div *ngIf="loadError" class="alert alert-warning mb-2" role="alert">
    Could not load location data. Confirm the API implements paginated
    <code>GET /stocktake/location/live</code> (query params below) and try again.
  </div>

  <!-- Paginator outside .table-responsive so it is not clipped or stuck in the horizontal scroll area -->
  <mat-paginator
    class="location-paginator"
    [length]="totalCount"
    [pageIndex]="page - 1"
    [pageSize]="limit"
    [pageSizeOptions]="[10, 15, 20, 50]"
    showFirstLastButtons
    (page)="onPageChange($event)"
    aria-label="Select page of location stock lines"
  >
  </mat-paginator>

  <div class="position-relative table-responsive">
    <mat-progress-bar *ngIf="loading" mode="indeterminate"></mat-progress-bar>

    <table
      mat-table
      matSort
      [dataSource]="dataSource"
      class="w-100 mat-elevation-z1"
      (matSortChange)="onSortChange($event)"
    >
      <ng-container matColumnDef="medicineName">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>Medication</th>
        <td mat-cell *matCellDef="let row">{{ row.medicineName }}</td>
      </ng-container>

      <ng-container matColumnDef="itemLabel">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>Item</th>
        <td mat-cell *matCellDef="let row">{{ row.itemLabel || '—' }}</td>
      </ng-container>

      <ng-container matColumnDef="strengthLabel">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>Strength</th>
        <td mat-cell *matCellDef="let row">{{ row.strengthLabel || '—' }}</td>
      </ng-container>

      <ng-container matColumnDef="packagingLabel">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>Packaging</th>
        <td mat-cell *matCellDef="let row">{{ row.packagingLabel || '—' }}</td>
      </ng-container>

      <ng-container matColumnDef="packSize">
        <th mat-header-cell *matHeaderCellDef>Pack size</th>
        <td mat-cell *matCellDef="let row">{{ row.packSizeLabel || '—' }}</td>
      </ng-container>

      <ng-container matColumnDef="onHold">
        <th
          mat-header-cell
          *matHeaderCellDef
          mat-sort-header
          class="col-qty"
          [matTooltip]="qtyHeaderTooltips.onHold"
          matTooltipShowDelay="300"
        >
          On hold
        </th>
        <td
          mat-cell
          *matCellDef="let row"
          class="col-qty"
          [class.qty-positive]="row.quantities.onHold > 0"
        >
          {{ row.quantities.onHold }}
        </td>
      </ng-container>

      <ng-container matColumnDef="toBeSentOut">
        <th
          mat-header-cell
          *matHeaderCellDef
          mat-sort-header
          class="col-qty"
          [matTooltip]="qtyHeaderTooltips.toBeSentOut"
          matTooltipShowDelay="300"
        >
          To be sent out
        </th>
        <td
          mat-cell
          *matCellDef="let row"
          class="col-qty"
          [class.qty-positive]="row.quantities.toBeSentOut > 0"
        >
          {{ row.quantities.toBeSentOut }}
        </td>
      </ng-container>

      <ng-container matColumnDef="captured">
        <th
          mat-header-cell
          *matHeaderCellDef
          mat-sort-header
          class="col-qty"
          [matTooltip]="qtyHeaderTooltips.captured"
          matTooltipShowDelay="300"
        >
          Captured
        </th>
        <td
          mat-cell
          *matCellDef="let row"
          class="col-qty"
          [class.qty-positive]="row.quantities.captured > 0"
        >
          {{ row.quantities.captured }}
        </td>
      </ng-container>

      <ng-container matColumnDef="reserved">
        <th
          mat-header-cell
          *matHeaderCellDef
          mat-sort-header
          class="col-qty"
          [matTooltip]="qtyHeaderTooltips.reserved"
          matTooltipShowDelay="300"
        >
          Reserved
        </th>
        <td
          mat-cell
          *matCellDef="let row"
          class="col-qty"
          [class.qty-positive]="row.quantities.reserved > 0"
        >
          {{ row.quantities.reserved }}
        </td>
      </ng-container>

      <ng-container matColumnDef="notYetIssued">
        <th
          mat-header-cell
          *matHeaderCellDef
          mat-sort-header
          class="col-qty"
          [matTooltip]="qtyHeaderTooltips.notYetIssued"
          matTooltipShowDelay="300"
        >
          Not yet issued
        </th>
        <td
          mat-cell
          *matCellDef="let row"
          class="col-qty"
          [class.qty-positive]="row.quantities.notYetIssued > 0"
        >
          {{ row.quantities.notYetIssued }}
        </td>
      </ng-container>

      <ng-container matColumnDef="total">
        <th
          mat-header-cell
          *matHeaderCellDef
          mat-sort-header
          class="col-qty"
          [matTooltip]="qtyHeaderTooltips.total"
          matTooltipShowDelay="300"
        >
          Total
        </th>
        <td mat-cell *matCellDef="let row" class="col-qty fw-bold">
          {{ lineTotal(row) }}
        </td>
      </ng-container>

      <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
      <tr
        mat-row
        *matRowDef="let row; columns: displayedColumns; trackBy: trackByRow"
      ></tr>
    </table>

    <p
      *ngIf="!loading && dataSource.data.length === 0"
      class="p-4 text-center text-muted mb-0"
    >
      No rows match your search or the list is empty.
    </p>
  </div>
</mat-card>
